'use client'

import { cn } from '@brew/ui/cn'
import type { MouseEvent, ReactNode } from 'react'
import { isPlainClick } from '@/lib/ui/plain-click'
import { MarketingContainer } from './marketing-container'
import {
  MARKETING_GUTTER_X,
  MARKETING_SECTION_HEADING,
  MARKETING_SECTION_Y,
} from './marketing-layout'
import { ProminentCtaButton } from './prominent-cta-button'
import { SectionTopDivider } from './section-divider'
import { useOpenAuthModal } from './use-auth-modal'

const SIGN_UP_HREF = '/sign-up'

export type FinalCtaProps = {
  heading?: ReactNode
  subheading?: ReactNode
  ctaLabel?: string
  actions?: ReactNode
  onCta?: () => void
  className?: string
  /** Optional decorative element rendered below the copy (e.g. a logo). */
  visual?: ReactNode
  /** Hairline top divider. Off where the section follows a divider-less page. */
  divider?: boolean
}

export function FinalCta({
  heading = 'Start sending with Brew.',
  subheading = 'No credit card. Free credits to try.',
  ctaLabel = 'Get started',
  actions,
  onCta,
  className,
  visual,
  divider = true,
}: FinalCtaProps) {
  const openAuth = useOpenAuthModal()

  const handleClick = () => {
    if (onCta) {
      onCta()
      return
    }
    openAuth()
  }

  // The default CTA is a real link to sign-up, so it works before hydration
  // and without JavaScript; once hydrated a plain click opens the modal (or
  // goes to /home when signed in) in place, while a Ctrl/Cmd/middle click
  // still opens /sign-up in a new tab.
  const handleLinkClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!isPlainClick(event)) {
      return
    }
    event.preventDefault()
    handleClick()
  }

  return (
    <section
      className={cn(
        'relative w-full overflow-hidden',
        MARKETING_GUTTER_X,
        MARKETING_SECTION_Y,
        className
      )}
    >
      {divider ? <SectionTopDivider /> : null}
      <MarketingContainer
        size="prose"
        className="relative z-10 flex flex-col items-center gap-6 text-center"
      >
        <h2 className={cn(MARKETING_SECTION_HEADING, 'text-foreground')}>
          {heading}
        </h2>
        {actions ??
          (onCta ? (
            <ProminentCtaButton onClick={handleClick}>
              {ctaLabel}
            </ProminentCtaButton>
          ) : (
            <ProminentCtaButton href={SIGN_UP_HREF} onClick={handleLinkClick}>
              {ctaLabel}
            </ProminentCtaButton>
          ))}
        {subheading ? (
          <p className="text-muted-foreground text-sm">{subheading}</p>
        ) : null}
        {visual}
      </MarketingContainer>
    </section>
  )
}
